/**
 * /automate/new — redirect alias to the New Workflow wizard.
 *
 * #1102 / #213 — Phase 2 QA found that direct navigation to
 * `/automate/new` returned the global Not Found page even though the
 * route is referenced by:
 *   - the Automate retest matrix as a required surface
 *   - the Phase 2 baseline checklist (`AUTO-ROUTE-002`)
 *   - operator bookmarks / deep-link expectations
 *
 * The modal-driven creation path lives at `/automate?action=create`
 * (see `routes/automate.tsx` — the `useEffect` watches `search.action`
 * and opens `<CreateWorkflowDialog>`). This file is a thin TanStack
 * file-route whose only job is to redirect into that flow so the
 * direct URL is no longer a 404.
 *
 * Why a redirect (not a brand-new page):
 *   - The wizard already exists and is invoked from /automate.
 *   - Building a parallel page would split the "create workflow"
 *     code path and risk drift between modal-create and route-create.
 *   - A redirect is the smallest possible fix that closes the bug
 *     without touching the wizard, the parent layout, or shared state.
 */
import { createFileRoute, redirect } from '@tanstack/react-router';

export const Route = createFileRoute('/automate/new')({
  beforeLoad: () => {
    // Send the user into the existing New Workflow dialog hosted on
    // the parent /automate route. `routes/automate.tsx` already
    // handles the `?action=create` query string and opens the wizard
    // automatically — see the useEffect on search.action there.
    throw redirect({
      to: '/automate',
      search: { action: 'create' },
    });
  },
  // beforeLoad throws before component mount, but TanStack still
  // requires a component to be declared. Render nothing as a defensive
  // fallback in case the redirect ever short-circuits during HMR.
  component: () => null,
});
